import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import JsBarcode from "jsbarcode";
import { Printer, ArrowLeft } from "@phosphor-icons/react";

export default function PrintLabels() {
  const [params] = useSearchParams();

  const ids = useMemo(
    () => (params.get("ids") || "").split(",").filter(Boolean),
    [params]
  );

  const [items, setItems] = useState([]);
  const [all, setAll] = useState([]);
  const [picked, setPicked] = useState(ids);

  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/inventory");
      setAll(data);
    })();
  }, []);

  useEffect(() => {
    setItems(all.filter((i) => picked.includes(i.id)));
  }, [all, picked]);

  const toggle = (id) =>
    setPicked((p) =>
      p.includes(id) ? p.filter((x) => x !== id) : [...p, id]
    );

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 print-root">
      <style>{`
        @page {
          size: 50.8mm 25.4mm;
          margin: 0;
        }

        @media print {
          html,
          body {
            width: 50.8mm !important;
            height: 25.4mm !important;
            background: #ffffff !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: hidden !important;
          }

          body * {
            visibility: hidden !important;
          }

          .label-sheet,
          .label-sheet * {
            visibility: visible !important;
          }

          .label-sheet {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 50.8mm !important;
            margin: 0 !important;
            padding: 0 !important;
            display: block !important;
            background: #ffffff !important;
            box-shadow: none !important;
          }

          .print-label {
            width: 50.8mm !important;
            height: 25.4mm !important;
            margin: 0 !important;
            padding: 0.5mm !important;
            box-sizing: border-box !important;
            overflow: hidden !important;
            background: #ffffff !important;
            color: #000000 !important;
            border: none !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            page-break-after: always !important;
            break-after: page !important;
          }

          .print-label:last-child {
            page-break-after: auto !important;
            break-after: auto !important;
          }

          .print-label svg {
            width: 49.8mm !important;
            height: 17mm !important;
            display: block !important;
          }
        }
      `}</style>

      <div className="max-w-7xl mx-auto p-6 md:p-8 no-print">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <button
              onClick={() => nav(-1)}
              className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-[#F5A623]"
            >
              <ArrowLeft size={14} />
              Back
            </button>

            <div className="mt-2 text-[10px] tracking-[0.3em] uppercase text-[#F5A623]">
              Label Sheet
            </div>

            <h1 className="mt-1 text-4xl font-bold tracking-tight">
              Print Barcode Labels
            </h1>

            <p className="text-sm text-zinc-500 mt-1">
              CODE128 · Rongta 50.8×25.4mm · Short Asset Tags
            </p>
          </div>

          <button
            onClick={() => window.print()}
            disabled={items.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-[#F5A623] hover:bg-[#E67E22] text-zinc-950 font-bold text-sm transition-colors disabled:opacity-60"
          >
            <Printer size={16} weight="bold" />
            Print {items.length > 0 ? `(${items.length})` : ""}
          </button>
        </div>

        <div className="mt-6 rounded-lg bg-zinc-900/50 border border-zinc-800 p-4 max-h-72 overflow-y-auto">
          <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-3">
            Select assets ({picked.length})
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {all.map((i) => (
              <label
                key={i.id}
                className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors ${
                  picked.includes(i.id)
                    ? "border-[#F5A623] bg-[#F5A623]/10"
                    : "border-zinc-800 hover:bg-zinc-800/30"
                }`}
              >
                <input
                  type="checkbox"
                  checked={picked.includes(i.id)}
                  onChange={() => toggle(i.id)}
                  className="accent-[#F5A623]"
                />

                <div className="min-w-0">
                  <div className="text-xs text-zinc-100 truncate">
                    {i.name}
                  </div>
                  <div className="text-[10px] text-zinc-500 font-mono truncate">
                    {i.asset_id || "—"} · {i.sku}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {items.length === 0 && (
          <div className="mt-6 p-10 text-center text-sm text-zinc-500 border border-dashed border-zinc-800 rounded-lg">
            Pick at least one asset to render its label.
          </div>
        )}
      </div>

      <div className="label-sheet flex flex-wrap max-w-7xl mx-auto px-6 pb-12">
        {items.map((i) => (
          <LabelCard key={i.id} item={i} />
        ))}
      </div>
    </div>
  );
}

function LabelCard({ item }) {
  const svgRef = useRef(null);

  const assetTag =
    item.asset_tag ||
    item.asset_id ||
    `KG-${String(item.id).replace(/-/g, "").slice(0, 6).toUpperCase()}`;

  useEffect(() => {
    if (!svgRef.current) return;

    try {
      JsBarcode(svgRef.current, assetTag, {
        format: "CODE128",
        width: 3.2,
        height: 60,
        displayValue: true,
        fontSize: 12,
        textMargin: 2,
        margin: 0,
        background: "#ffffff",
        lineColor: "#000000",
        font: "monospace",
      });
    } catch (e) {
      svgRef.current.innerHTML = "";
    }
  }, [assetTag]);

  return (
    <div
      className="print-label bg-white text-black border border-zinc-300 rounded"
      style={{
        width: "50.8mm",
        height: "25.4mm",
        padding: "0.5mm",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            lineHeight: 1,
          }}
        >
          <span
            style={{
              fontSize: "6.5pt",
              fontWeight: 800,
              letterSpacing: "0.08em",
            }}
          >
            KERMA GAMES
          </span>

          <span
            style={{
              fontSize: "5.2pt",
              fontFamily: "monospace",
              color: "#444",
            }}
          >
            {item.location || ""}
            {item.floor ? ` · ${item.floor}` : ""}
          </span>
        </div>

        <svg
          ref={svgRef}
          style={{
            width: "49.8mm",
            height: "17mm",
            display: "block",
          }}
        />

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "2mm",
            lineHeight: 1.05,
          }}
        >
          <span
            style={{
              fontSize: "5.5pt",
              fontWeight: 700,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
            }}
          >
            {item.name}
          </span>

          {item.serial_number && (
            <span
              style={{
                fontSize: "5pt",
                fontFamily: "monospace",
                color: "#444",
                whiteSpace: "nowrap",
              }}
            >
              SN: {item.serial_number}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
